import React from 'react';
import { Loader2, Check, Plus, AlertCircle } from 'lucide-react';
import { useAddButtonState } from '../../hooks/useAddButtonState';

/**
 * Uniform AddButton component that provides consistent UX across the application
 * Handles "Add to [Category]" -> "Adding..." -> "Added" pattern
 */
const AddButton = ({
  buttonId,
  onAdd,
  addText = "Add",
  addingText = "Adding...",
  addedText = "Added",
  errorText = "Try Again",
  className = "",
  disabled = false,
  variant = "primary", // primary, secondary, success, danger
  size = "md", // sm, md, lg
  icon: CustomIcon = null,
  showSuccessIcon = true,
  ...props
}) => {
  const { BUTTON_STATES, getButtonState, handleAddOperation } = useAddButtonState();

  const currentState = getButtonState(buttonId);

  // Handle click event
  const handleClick = async () => {
    if (disabled || currentState === BUTTON_STATES.LOADING || currentState === BUTTON_STATES.SUCCESS) {
      return;
    }

    await handleAddOperation(buttonId, onAdd, {
      successTimeout: 2000,
      onError: (error) => {
        console.error('Add operation failed:', error);
      }
    });
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (currentState) {
      case BUTTON_STATES.LOADING:
        return addingText;
      case BUTTON_STATES.SUCCESS:
        return addedText;
      case BUTTON_STATES.ERROR:
        return errorText;
      default:
        return addText;
    }
  };

  // Get button icon based on state
  const getButtonIcon = () => {
    switch (currentState) {
      case BUTTON_STATES.LOADING:
        return <Loader2 className="w-4 h-4 animate-spin" />;
      case BUTTON_STATES.SUCCESS:
        return showSuccessIcon ? <Check className="w-4 h-4" /> : null;
      case BUTTON_STATES.ERROR:
        return <AlertCircle className="w-4 h-4" />;
      default:
        return CustomIcon ? <CustomIcon className="w-4 h-4" /> : <Plus className="w-4 h-4" />;
    }
  };

  // Get button styles based on variant and state
  const getButtonStyles = () => {
    const baseStyles = "inline-flex items-center justify-center space-x-2 font-medium rounded transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
    
    // Size styles
    const sizeStyles = {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-1.5 text-sm", 
      lg: "px-4 py-2 text-base"
    };

    // State-based styles
    if (currentState === BUTTON_STATES.SUCCESS) {
      return `${baseStyles} ${sizeStyles[size]} bg-green-600 text-white cursor-default`;
    }

    if (currentState === BUTTON_STATES.ERROR) {
      return `${baseStyles} ${sizeStyles[size]} bg-red-600 text-white hover:bg-red-700 focus:ring-red-500`;
    }

    if (currentState === BUTTON_STATES.LOADING) {
      return `${baseStyles} ${sizeStyles[size]} bg-gray-400 text-white cursor-not-allowed`;
    }

    // Variant styles for idle state
    const variantStyles = {
      primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
      secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500",
      success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
      danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
    };

    const disabledStyles = disabled ? "opacity-50 cursor-not-allowed" : "";

    return `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${disabledStyles}`;
  };

  const buttonIcon = getButtonIcon();

  return (
    <button
      onClick={handleClick}
      disabled={disabled || currentState === BUTTON_STATES.LOADING || currentState === BUTTON_STATES.SUCCESS}
      className={`${getButtonStyles()} ${className}`}
      {...props}
    >
      {buttonIcon}
      <span>{getButtonText()}</span>
    </button>
  );
};

export default AddButton;